/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { typography2 } from '@coursera/cds-core';
import { AttachmentIcon, NoteIcon } from '@coursera/cds-icons';

import { AssetTypeNames } from 'bundles/asset-admin/types/assets';
import type { AssetData } from 'bundles/cml/shared/types/assetDataTypes';

import _t from 'i18n!nls/cml';

const styles = {
  link: css`
    color: initial !important;
    text-decoration: initial !important;
  `,
  root: css`
    background-color: var(--cds-color-neutral-background-primary-weak);
    border-radius: 8px;
    height: 84px;
    padding: 0 var(--cds-spacing-300);
    display: flex;
    align-items: center;

    &:hover {
      cursor: pointer;
      outline: 2px solid var(--cds-color-interactive-primary);
    }
  `,
  assetInfo: css`
    flex: 1;
    min-width: 0;
    flex-direction: column;
    white-space: nowrap;
    overflow-x: hidden;
    margin-left: var(--cds-spacing-200);
  `,
  assetName: css`
    ${typography2.subtitleMedium}
    text-overflow: ellipsis;
    overflow: hidden;
  `,
  assetLabel: css`
    ${typography2.bodyPrimary}
    color: var(--cds-color-neutral-primary-weak);
    margin-top: var(--cds-spacing-50);
  `,
};

type Props = {
  id: string;
  assetData: AssetData;
  readonly?: boolean;
};

const NonMediaAsset = (props: Omit<Props, 'readonly'>) => {
  const { assetData, id } = props;

  if (!assetData) {
    return null;
  }

  const { extension, name, type: assetType, url } = assetData;
  const fileExtension = extension || name.split('.').splice(-1)[0] || '';
  const assetLabel = `${fileExtension.toUpperCase()} File`;
  const AssetIcon = assetType === AssetTypeNames.PDF ? NoteIcon : AttachmentIcon;

  return (
    <div
      className={`cml-asset cml-asset-${assetType}`}
      data-id={id}
      data-url={url}
      data-extension={fileExtension}
      data-name={name}
      css={styles.root}
    >
      <div>
        <AssetIcon
          size="large"
          title={
            assetType === 'generic'
              ? _t(`Download #{assetLabel}`, { assetLabel })
              : _t(`View #{assetLabel}`, { assetLabel })
          }
        />
      </div>
      <div css={styles.assetInfo}>
        <div css={styles.assetName}>{name}</div>
        <div css={styles.assetLabel}>{assetLabel}</div>
      </div>
    </div>
  );
};

const NonMediaAssetProvider = ({ readonly, ...props }: Props) => {
  const { assetData } = props;
  if (!assetData) {
    return null;
  }

  if (!readonly) {
    return <NonMediaAsset {...props} />;
  }

  return (
    <a css={styles.link} href={assetData.url} target="_blank" rel="noopener" data-e2e="asset-download-link">
      <NonMediaAsset {...props} />
    </a>
  );
};

export default NonMediaAssetProvider;
